import React from 'react';
import type {TaskAssignmentSimpleResponse} from 'src/api/ProactApiClient';
import {FormTransitionBar} from 'src/app/forms/FormTransitionBar';
import {TaskAssignmentSupportingSections} from 'src/app/tasks/TaskAssignmentSupportingSections';
import {useTaskAssignmentView} from 'src/app/tasks/useTaskAssignmentView';
import {TaskFeatureAssignmentView} from 'src/app/tasks/TaskFeatureViews';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {ErrorAlert} from 'src/design/molecules/ErrorAlert';
import {SideDrawer} from 'src/design/organisms/SideDrawer';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

export interface TaskAssignmentPreviewProps {
  item: TaskAssignmentSimpleResponse;
  onClose: () => void;
}

export const TaskAssignmentPreview: React.FC<TaskAssignmentPreviewProps> = ({
  item,
  onClose,
}) => {
  const taskType = item.taskDefinition.taskType;
  const {viewItem, isLoading, error, refetch, machine, pills, postTransition} =
    useTaskAssignmentView(item.id);

  return (
    <>
      <SideDrawer.Header
        title={viewItem?.taskDefinition.subject ?? item.taskDefinition.subject}
        badges={pills}
        fullPageHref={PathUtility.ViewTaskAssignment(taskType, item.id)}
      />
      <SideDrawer.Body loading={isLoading}>
        {error ? (
          <ErrorAlert mt={0} message={parseErrorMessage(error)} />
        ) : viewItem ? (
          <Stack gap="md">
            <TaskFeatureAssignmentView item={viewItem} />
            <TaskAssignmentSupportingSections viewItem={viewItem} />
          </Stack>
        ) : (
          !isLoading && (
            <Text c="dimmed" size="sm">
              This task is no longer available.
            </Text>
          )
        )}
      </SideDrawer.Body>
      {machine && viewItem && (
        <SideDrawer.Footer>
          <FormTransitionBar
            stateMachine={machine}
            stateId={viewItem.state}
            transitionBaseUrl={`task-assignment/${viewItem.id}/transition`}
            postTransition={postTransition}
            onOfflineQueued={onClose}
            onRedirect={onClose}
            objectType={viewItem.taskDefinition.taskType}
            onRefreshTransition={refetch}
            invalidateTagsOnComplete={['TaskAssignments']}
          />
        </SideDrawer.Footer>
      )}
    </>
  );
};
